import React, { useState } from "react";
import ImageBanner from "@/components/ImageBanner";
import Footer from "@/layout/Footer";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import DesktopVerifyOtpForm from "@/components/DesktopVerifyOtpForm";
import MobileVerifyOtpForm from "@/components/MobileVerifyOtpForm";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { authAction } from "@/redux/actions/auth.action";

const otpSchema = Yup.object().shape({
	otp: Yup.string()
		.required("Verification code is required")
		.matches(/^\d{6}$/, "Verification code must be exactly 6 digits"),
});

const VerifyOtp = () => {
	const [verifyOtpError, setVerifyOtpError] = useState(null);
	const [resendOtpError, setResendOtpError] = useState(null);

	const forgotPassword = useSelector((state) => state.forgotPassword);
	const email = forgotPassword?.email || "";

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const desktopForm = useForm({
		resolver: yupResolver(otpSchema),
		mode: "onChange",
	});

	const mobileForm = useForm({
		resolver: yupResolver(otpSchema),
		mode: "onChange",
	});

	const desktopOtp = desktopForm.watch("otp", "");
	const mobileOtp = mobileForm.watch("otp", "");

	React.useEffect(() => {
		if (desktopOtp?.length === 6) {
			desktopForm.handleSubmit(onSubmit)();
		}
	}, [desktopOtp]);

	React.useEffect(() => {
		if (mobileOtp?.length === 6) {
			mobileForm.handleSubmit(onSubmit)();
		}
	}, [mobileOtp]);

	const onSubmit = async (data) => {
		const otpVerifyData = { ...data, email: email };
		console.log("OTP submitted:", otpVerifyData);
		try {
			const result = await dispatch(authAction.verifyOtpCode(otpVerifyData));

			if (result?.success) {
				navigate("/reset-password");
			} else {
				setVerifyOtpError(result?.error || "Verify Otp failed");
			}
		} catch (error) {
			//console.error("Login failed:", error);
			setVerifyOtpError(
				error?.error || error.message || "An unexpected error occurred"
			);
		}
	};
	const resendOtpCode = async () => {
		const resendOtpData = { email: email };
		console.log("OTP Resend submitted:", resendOtpData);
		try {
			const result = await dispatch(authAction.resendOtpCode(resendOtpData));

			if (result?.success) {
				setResendOtpError(null);
				// Optionally, you can show a success message or update the UI
				// For example, you might want to reset the form or show a toast notification
				desktopForm.reset();
				mobileForm.reset();
			} else {
				setResendOtpError(result?.error || "ResendOtpCode  failed");
			}
		} catch (error) {
			//console.error("resendOtpCode failed:", error);
			setResendOtpError(
				error?.error || error.message || "An unexpected error occurred"
			);
		}
	};

	return (
		<>
			<div className="relative flex flex-col hidden lg:flex w-full h-auto items-center justify-center mt-[40px]">
				<div className="w-full max-w-[1440px] flex items-center justify-between px-8">
					<ImageBanner
						type="desktop"
						title="Reset Your Password"
						containerWidth="w-full max-w-[1440px] flex items-center"
						titlePosition={{ top: "top-[100px]", left: "left-[60px]" }}
					/>
				</div>
			</div>
			<div className="flex justify-center mt-[28px] h-[800px] hidden lg:flex">
				<div className="w-[361px] h-auto flex flex-col gap-[16px] rounded-[8px] pt-[16px] pr-[20px] pb-[32px] pl-[20px]">
					{verifyOtpError && (
						<div className="text-red-500 text-sm mb-4">{verifyOtpError}</div>
					)}
					{resendOtpError && (
						<div className="text-red-500 text-sm mb-4">{resendOtpError}</div>
					)}
					<DesktopVerifyOtpForm
						form={desktopForm}
						onSubmit={onSubmit}
						email={forgotPassword?.email}
						otp={desktopOtp}
					/>
					<div className="w-full h-[82px] flex flex-col items-center justify-center mx-auto gap-[16px] mt-[50px]">
						<button
							type="button"
							onClick={resendOtpCode}
							className="w-full h-[32px] flex items-center justify-center rounded-[24px] p-[8px] bg-deepForest text-white text-[13px] font-[500] font-montserrat leading-[24px] text-center"
						>
							Resend Code
						</button>
						<p className="text-[14px] font-[400] font-montserrat leading-[1] text-[#193D34] cursor-pointer mt-[40px]">
							Still need help?
						</p>
						<button
							type="button"
							className="w-full h-[32px] flex items-center justify-center rounded-[24px] p-[8px] bg-white text-deepForest border border-deepForest text-[13px] font-[500] font-montserrat leading-[24px] text-center"
						>
							Contact Support
						</button>
					</div>
				</div>
			</div>

			{/* Mobile View */}
			<ImageBanner
				type="mobile"
				title={`Reset Your\n Password`}
				mobileTitlePosition={{ top: "top-[200px]", left: "left-[10px]" }}
			/>
			<div className="flex lg:hidden justify-center mt-[28px] px-4">
				<div className="w-full max-w-[361px] h-auto flex flex-col gap-[16px] rounded-[8px] pt-[16px] pr-[20px] pb-[32px] pl-[20px]">
					{resendOtpError && (
						<div className="text-red-500 text-sm mb-4">{resendOtpError}</div>
					)}
					{verifyOtpError && (
						<div className="text-red-500 text-sm mb-4">{verifyOtpError}</div>
					)}
					<MobileVerifyOtpForm
						form={mobileForm}
						onSubmit={onSubmit}
						email={forgotPassword?.email}
						otp={mobileOtp}
					/>
					<div className="w-full flex flex-col items-center justify-center mx-auto gap-[16px] mt-[20px]">
						<button
							type="button"
							onClick={resendOtpCode}
							className="w-full h-[32px] flex items-center justify-center rounded-[24px] p-[8px] bg-deepForest text-white text-[13px] font-[500] font-montserrat leading-[24px] text-center"
						>
							Resend Code
						</button>
						<p className="text-[14px] font-[400] font-montserrat leading-[1] text-[#193D34] cursor-pointer mt-[40px]">
							Still need help?
						</p>
						<button
							type="button"
							className="w-full h-[32px] flex items-center justify-center rounded-[24px] p-[8px] bg-white text-deepForest border border-deepForest text-[13px] font-[500] font-montserrat leading-[24px] text-center"
						>
							Contact Support
						</button>
					</div>
				</div>
			</div>
			<Footer />
		</>
	);
};

export default VerifyOtp;
